import { Link } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

// Map dot-notation keys from the Slice-B structured block to readable labels.
const FIELD_LABELS = {
  'billing_provider_profile.organization_name': 'Organization name',
  'billing_provider_profile.first_name': 'First name',
  'billing_provider_profile.last_name': 'Last name',
  'billing_provider_profile.npi': 'NPI',
  'billing_provider_profile.tax_id': 'Tax ID',
  'billing_provider_profile.address_line_1': 'Street',
  'billing_provider_profile.city': 'City',
  'billing_provider_profile.state': 'State',
  'billing_provider_profile.zip_code': 'ZIP code',
  'billing_provider_profile.phone': 'Phone',
};

function labelFor(key) {
  if (FIELD_LABELS[key]) return FIELD_LABELS[key];
  // Fall back to the trailing segment, prettified.
  const tail = String(key).split('.').pop() || key;
  return tail.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Slice C2 — inline prompt shown when submit returns the structured block
 * { error_code: 'OPERATOR_PROFILE_INCOMPLETE', missing, billing_provider_profile_id }.
 *
 * Props:
 *  - missing: string[]  (dot-notation field keys)
 *  - billingProviderProfileId: string | null
 *  - onRetry: () => Promise  (re-runs the server-side resolver via handleSubmit)
 *  - retrying: boolean
 */
export default function CompleteProfilePrompt({ missing, billingProviderProfileId, onRetry, retrying }) {
  const keys = Array.isArray(missing) ? missing : [];

  return (
    <Alert severity="warning" sx={{ mb: 2 }} data-testid="complete-profile-prompt">
      <AlertTitle>Complete this billing provider</AlertTitle>
      {billingProviderProfileId ? (
        <Typography variant="body2" sx={{ mb: 1 }}>
          The tied billing provider is missing required information. Add the
          following before submitting:
        </Typography>
      ) : (
        <Typography variant="body2" sx={{ mb: 1 }}>
          No billing provider is tied to this claim. Tie one with the picker
          above, or complete a provider&apos;s details, then retry. Missing:
        </Typography>
      )}

      {keys.length > 0 && (
        <Box component="ul" sx={{ mt: 0, mb: 1, pl: 3 }}>
          {keys.map((k) => (
            <li key={k}>
              <Typography variant="body2" component="span">{labelFor(k)}</Typography>
            </li>
          ))}
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          component={Link}
          to="/providers"
        >
          Open Billing Providers
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={onRetry}
          disabled={retrying}
          data-testid="retry-submit"
        >
          {retrying ? 'Retrying…' : 'Retry submit'}
        </Button>
      </Box>
    </Alert>
  );
}
